import { ReactNode } from 'react';
import { useRecoilValue } from 'recoil';
import { PendingChangesRecoilBridge } from '~/domain/adapters/PendingChangesRecoilBridge';
import { RecoilBridge } from '~/domain/adapters/RecoilBridge';
import { useDomainEventHandlers } from '~/domain/events/handlers/useDomainEventHandlers';
import useSelectedPatient from '~/hooks/patient/useSelectedPatient';
import { useEditorPresence } from '~/hooks/save/useEditorPresence';
import { useUnsavedChangesGuard } from '~/hooks/save/useUnsavedChangesGuard';
import { connectorPatientManifestState } from '~/state/atoms/connectorManifestAtoms';
import { viewManagerState } from '~/state/atoms/engineAtoms';
import { AppServicesProvider } from './AppServicesContext';
import { PatientSessionProvider } from './SessionContext';

/**
 * Reads Recoil state to obtain the current ViewManager instance and selected
 * patient, then mounts AppServicesProvider → PatientSessionProvider →
 * RecoilBridge around the existing tree.
 *
 * Must be rendered inside RecoilRoot because both viewManagerState and
 * useSelectedPatient() depend on Recoil atoms.
 *
 * The providers render unconditionally so the React tree SHAPE never
 * depends on async engine state — flipping a fragment to a provider tree
 * when the engine initialized forced React to unmount and remount the
 * entire app subtree mid-session-initialization. Before the engine
 * exists the AppServices context value is simply null and no patient
 * session is created; domain event handlers and the RecoilBridge mount
 * from app start (master parity).
 */
export function AppServicesAndSessionBridge({
    children,
}: {
    children: ReactNode;
}) {
    const viewManager = useRecoilValue(viewManagerState);
    const patient = useSelectedPatient() ?? null;
    const connectorManifest = useRecoilValue(connectorPatientManifestState);

    return (
        <AppServicesProvider engine={viewManager}>
            <PatientSessionProvider
                patient={patient}
                connectorManifest={connectorManifest}
            >
                <DomainEventHandlersMount />
                <RecoilBridge />
                <PendingChangesRecoilBridge />
                <UnsavedChangesGuardMount />
                {children}
            </PatientSessionProvider>
        </AppServicesProvider>
    );
}

/**
 * Mounts {@link useDomainEventHandlers} inside the provider tree so the
 * handlers can read the active patient session via {@link useSessionOrNull}.
 * Renders nothing.
 */
function DomainEventHandlersMount() {
    useDomainEventHandlers();
    return null;
}

/**
 * Mounts the tab-close guard and the advisory editor-presence
 * registration. Renders nothing.
 */
function UnsavedChangesGuardMount() {
    useUnsavedChangesGuard();
    useEditorPresence();
    return null;
}
